import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import operatorConsoleService from '../../services/operatorConsoleService';

// The operator control plane — §20.3 Placements, §20.4 Queries, §20.5 Migrations,
// §20.6 Schemas, §20.7 Security.
//
// One page with tabs rather than five sidebar entries, because these five views are
// read together during the one activity they exist for: moving a tenant, or
// understanding why a cluster is behaving oddly. Splitting them across the sidebar
// makes an operator navigate between facts that belong side by side.
//
// The rule that shapes every tab: a capability the platform does not have is shown
// as ABSENT WITH ITS REASON, never as an empty table. An empty "running queries"
// table says "nothing is running"; the truth is "we do not track that", and those
// call for completely different actions from whoever is reading it.

const TABS = [
    { key: 'placements', label: 'Placements' },
    { key: 'queries', label: 'Queries' },
    { key: 'migrations', label: 'Migrations' },
    { key: 'schemas', label: 'Schemas' },
    { key: 'security', label: 'Security' },
];

const Unavailable = ({ what, reason }) => (
    // Deliberately `info`, not `warning`. The platform is not broken; it is not
    // instrumented, and colouring that as an incident trains people to ignore the
    // colour on the day it means something.
    <Alert severity="info" sx={{ mt: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {what} is not available
        </Typography>
        <Typography variant="body2">{reason}</Typography>
    </Alert>
);

const Note = ({ children }) =>
    children ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {children}
        </Typography>
    ) : null;

const PlacementsTab = ({ data }) => {
    if (data?.error) return <Alert severity="error">{data.error}</Alert>;
    const tenants = data?.tenants || [];

    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${data?.count ?? 0} tenants`} />
                {data?.imbalance != null && (
                    <Tooltip title="Busiest cell's tenant count divided by the quietest">
                        <Chip
                            size="small"
                            variant="outlined"
                            color={data.imbalance > 3 ? 'warning' : 'default'}
                            label={`imbalance ${
                                data.imbalance === 'Infinity' || !Number.isFinite(data.imbalance)
                                    ? '∞'
                                    : Number(data.imbalance).toFixed(1)
                            }×`}
                        />
                    </Tooltip>
                )}
            </Stack>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Tenant</TableCell>
                        <TableCell>Current cell</TableCell>
                        <TableCell>RLS</TableCell>
                        <TableCell>Migration</TableCell>
                        <TableCell>Alert rules</TableCell>
                        <TableCell>Recommendation</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tenants.map((t) => (
                        <TableRow key={t.orgId} hover>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {t.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    #{t.orgId}
                                </Typography>
                            </TableCell>
                            <TableCell>{t.currentCell || <em>none</em>}</TableCell>
                            <TableCell>
                                <Tooltip title={t.rlsNote || ''}>
                                    <Chip
                                        size="small"
                                        label={t.rlsStatus}
                                        color={t.rlsStatus === 'NO_CELL' ? 'error' : 'default'}
                                        variant="outlined"
                                    />
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                {t.migrationState === 'NONE' ? (
                                    <Typography variant="caption" color="text.secondary">
                                        —
                                    </Typography>
                                ) : (
                                    <Chip size="small" label={t.migrationState} />
                                )}
                            </TableCell>
                            <TableCell>{t.alertRules}</TableCell>
                            <TableCell sx={{ maxWidth: 420 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t.recommendation}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Note>{data?.note}</Note>

            {/* The §20.3 action vocabulary, with the two steps that have no executor
                marked. Showing the whole list makes the shape of the workflow legible
                even where a step is not yet buildable. */}
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
                Guided actions
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {(data?.guidedActions || []).map((a) => (
                    <Tooltip key={a.label} title={a.reason || a.endpoint || ''}>
                        <Chip
                            size="small"
                            label={a.label}
                            variant="outlined"
                            color={a.available ? 'default' : 'warning'}
                        />
                    </Tooltip>
                ))}
            </Stack>
        </Box>
    );
};

const QueriesTab = ({ data }) => (
    <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
            {data?.scopeNote}
        </Alert>

        <Typography variant="overline" color="text.secondary">
            Workload classes
        </Typography>
        <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
                <TableRow>
                    <TableCell>Class</TableCell>
                    <TableCell align="right">Max concurrent / tenant</TableCell>
                    <TableCell align="right">Max execution</TableCell>
                    <TableCell>Latency sensitive</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {(data?.workloadClasses || []).map((c) => (
                    <TableRow key={c.workloadClass} hover>
                        <TableCell>{c.workloadClass}</TableCell>
                        <TableCell align="right">{c.maxConcurrentPerTenant}</TableCell>
                        <TableCell align="right">{c.maxExecutionSeconds}s</TableCell>
                        <TableCell>{c.latencySensitive ? 'yes' : 'no'}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>

        {data?.runningQueriesAvailable === false && (
            <Unavailable what="A per-query list" reason={data.runningQueriesReason} />
        )}

        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            Actions
        </Typography>
        {(data?.actions || []).map((a) => (
            <Box key={a.label} sx={{ mt: 1 }}>
                <Chip
                    size="small"
                    label={a.label}
                    variant="outlined"
                    color={a.available ? 'default' : 'warning'}
                    sx={{ mr: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                    {a.reason}
                </Typography>
            </Box>
        ))}
    </Box>
);

const MigrationsTab = ({ data }) => {
    const operations = data?.operations || [];
    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip size="small" label={`${operations.length} in flight`} />
                {(data?.awaitingOperator ?? 0) > 0 && (
                    <Chip
                        size="small"
                        color="warning"
                        label={`${data.awaitingOperator} awaiting a decision`}
                    />
                )}
            </Stack>

            {operations.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Nothing is in flight.
                </Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Operation</TableCell>
                            <TableCell>Tenant</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell>Permitted next</TableCell>
                            <TableCell>Approved</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {operations.map((o) => (
                            <TableRow key={o.id} hover>
                                <TableCell>
                                    {o.operation_type}
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        #{o.id} · {o.requested_by}
                                    </Typography>
                                </TableCell>
                                <TableCell>{o.org_id ?? '—'}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={o.state}
                                        color={o.awaitsOperator ? 'warning' : 'default'}
                                    />
                                </TableCell>
                                <TableCell>
                                    {(o.allowedNext || []).map((n) => (
                                        <Chip
                                            key={n}
                                            size="small"
                                            variant="outlined"
                                            label={n}
                                            sx={{ mr: 0.5 }}
                                            // The backend names the destructive
                                            // transitions; the console does not
                                            // decide that for itself.
                                            color={
                                                (data?.confirmationRequired || []).includes(n)
                                                    ? 'error'
                                                    : 'default'
                                            }
                                        />
                                    ))}
                                </TableCell>
                                <TableCell>{o.cutover_approved ? 'yes' : 'no'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Note>{data?.confirmationNote}</Note>
            {data?.progressAvailable === false && (
                <Unavailable what="Progress reporting" reason={data.progressReason} />
            )}
        </Box>
    );
};

const SchemasTab = ({ data }) => {
    if (data?.error) return <Alert severity="error">{data.error}</Alert>;
    return (
        <Box>
            <Chip
                size="small"
                sx={{ mb: 2 }}
                label={`desired version ${data?.desiredVersion ?? '?'}`}
            />

            <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Cluster</TableCell>
                        <TableCell align="right">Desired</TableCell>
                        <TableCell align="right">Actual</TableCell>
                        <TableCell>Drift</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(data?.clusters || []).map((c) => (
                        <TableRow key={c.id} hover>
                            <TableCell>{c.name}</TableCell>
                            <TableCell align="right">{c.desiredVersion}</TableCell>
                            {/* A null actual means NEVER MIGRATED, which is a
                                different thing from version 0 and must not render
                                as one. */}
                            <TableCell align="right">
                                {c.actualVersion == null ? <em>never</em> : c.actualVersion}
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                    {c.driftNote}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Typography variant="overline" color="text.secondary">
                Migration registry
            </Typography>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell align="right">V</TableCell>
                        <TableCell>Migration</TableCell>
                        <TableCell>Destructive</TableCell>
                        <TableCell>Rollback</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(data?.registry || []).map((m) => (
                        <TableRow key={m.version} hover>
                            <TableCell align="right">{m.version}</TableCell>
                            <TableCell>
                                {m.id}
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {m.description}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                {m.destructive ? (
                                    <Chip size="small" color="error" label="destructive" />
                                ) : (
                                    '—'
                                )}
                            </TableCell>
                            <TableCell>
                                {m.rollbackAvailable ? (
                                    'available'
                                ) : (
                                    <Typography variant="caption" color="warning.main">
                                        none
                                    </Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

const SecurityTab = ({ data }) => (
    <Box>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`${data?.enabledTenants ?? '?'} enabled tenants`} />
            <Chip
                size="small"
                variant="outlined"
                color={(data?.tenantsWithoutCell ?? 0) > 0 ? 'error' : 'default'}
                label={`${data?.tenantsWithoutCell ?? 0} without a cell`}
            />
        </Stack>
        <Note>{data?.tenantsWithoutCellNote}</Note>

        <Box sx={{ mt: 3 }}>
            <Typography variant="overline" color="text.secondary">
                Isolation proofs
            </Typography>
            <Box sx={{ mt: 1 }}>
                <Chip size="small" color="warning" label={`cross-tenant: ${data?.crossTenantTestStatus}`} />
                <Note>{data?.crossTenantTestNote}</Note>
            </Box>
            <Box sx={{ mt: 2 }}>
                <Chip
                    size="small"
                    color="warning"
                    label={`cache isolation: ${data?.cacheIsolationTestStatus}`}
                />
                <Note>{data?.cacheIsolationTestNote}</Note>
            </Box>
        </Box>

        {data?.deniedQueriesAvailable === false && (
            <Unavailable what="Denied-query inspection" reason={data.deniedQueriesReason} />
        )}

        <Note>{data?.coverageNote}</Note>
    </Box>
);

const OperatorControlPlanePage = () => {
    const theme = useTheme();
    const [tab, setTab] = useState(0);
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const key = TABS[tab].key;

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const result = await operatorConsoleService[key]();
            setData((prev) => ({ ...prev, [key]: result }));
        } catch (e) {
            setError(e?.response?.data?.error || e.message || 'Could not load this view.');
        } finally {
            setLoading(false);
        }
    }, [key]);

    useEffect(() => {
        load();
    }, [load]);

    const view = data[key];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Control Plane
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Placement, query load, migrations, schema drift and isolation posture.
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={load} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                {TABS.map((t) => (
                    <Tab key={t.key} label={t.label} />
                ))}
            </Tabs>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                {loading && !view ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {key === 'placements' && <PlacementsTab data={view} />}
                        {key === 'queries' && <QueriesTab data={view} />}
                        {key === 'migrations' && <MigrationsTab data={view} />}
                        {key === 'schemas' && <SchemasTab data={view} />}
                        {key === 'security' && <SecurityTab data={view} />}
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default OperatorControlPlanePage;
